import React, { useState, useMemo } from 'react';
import { CiFileOn } from "react-icons/ci";
import { IoClose } from 'react-icons/io5';

interface FileDetail {
    file: File;
    name: string;
    size: number;
}

const FileUploader: React.FC = () => {
    const [files, setFiles] = useState<FileDetail[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState<string | null>(null);

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        processFiles(event.dataTransfer.files);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            processFiles(event.target.files);
        }
    };

    const processFiles = (fileList: FileList) => {
        const filteredFiles = Array.from(fileList).filter(file =>
            file.name.endsWith('.json') || file.name.endsWith('.png')
        ).map(file => ({
            file,
            name: file.name,
            size: file.size,
        }));
        setFiles(prev => [...prev, ...filteredFiles]);
    };

    const removeFile = (fileName: string) => {
        setFiles(prev => prev.filter(file => file.name !== fileName));
    };

    const canRequest = useMemo(() => {
        const grouped = files.reduce((acc, file) => {
            const baseName = file.name.replace(/\..+$/, '');
            acc[baseName] = (acc[baseName] || []).concat(file.name);
            return acc;
        }, {} as Record<string, string[]>);

        return Object.values(grouped).some(names => {
            const base = names[0].replace(/\..+$/, '');
            return names.includes(`${base}.json`) && names.includes(`${base}.png`);
        });
    }, [files]);

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        setUploadMessage(null);

        const fileGroups = files.reduce((acc, fileDetail) => {
            const baseName = fileDetail.name.replace(/\..+$/, '');
            acc[baseName] = acc[baseName] || {};
            if (fileDetail.name.endsWith('.json')) {
                acc[baseName].jsonFile = fileDetail.file;
            } else if (fileDetail.name.endsWith('.png')) {
                acc[baseName].pngFile = fileDetail.file;
            }
            return acc;
        }, {} as Record<string, { jsonFile?: File; pngFile?: File }>);

        try {
            for (const [baseName, { jsonFile, pngFile }] of Object.entries(fileGroups)) {
                if (jsonFile && pngFile) {
                    const formDataPair = new FormData();
                    formDataPair.append('jsonFile', jsonFile);
                    formDataPair.append('pngFile', pngFile);

                    const response = await fetch('http://127.0.0.1:8080/files/upload', {
                        method: 'POST',
                        body: formDataPair,
                    });

                    if (!response.ok) {
                        const responseText = await response.text();
                        throw new Error(responseText || 'Failed to upload files.');
                    }

                    const blob = await response.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = 'ItemAdder.zip';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(downloadUrl);
                }
            }
            setUploadMessage('Files uploaded and downloaded successfully.');
        } catch (error: any) {
            console.error(error);
            setUploadMessage(error.message || 'An error occurred while uploading files.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <section className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                <div className="p-6 max-w-md mx-auto bg-white rounded-xl shadow-lg flex flex-col items-center space-y-4">
                    <div
                        onDrop={handleDrop}
                        onDragOver={e => e.preventDefault()}
                        className="flex flex-col items-center justify-center p-12 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50"
                    >
                        <img src={`${process.env.PUBLIC_URL}/logo/logo.png`} alt="upload" className="w-24 mb-3" />
                        Drag and drop your files here or
                        <label className="mt-2 bg-indigo-600 text-white px-4 py-2 rounded shadow cursor-pointer">
                            Browse files
                            <input
                                type="file"
                                multiple
                                accept=".png,.json"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </label>
                    </div>
                    <ul className="w-full">
                        {files.map(file => (
                            <li key={file.name} className="flex justify-between items-center px-2 py-1 border-b last:border-b-0">
                                <div className="flex items-center space-x-2">
                                    <CiFileOn />
                                    <span className="text-sm font-medium">{file.name} - {Math.round(file.size / 1024)} kb</span>
                                </div>
                                <button onClick={() => removeFile(file.name)} className="text-red-500">
                                    <IoClose />
                                </button>
                            </li>
                        ))}
                    </ul>
                    <button
                        onClick={handleUpload}
                        className={`mt-4 px-4 py-2 rounded ${canRequest ? 'bg-green-500' : 'bg-gray-300'}`}
                        disabled={!canRequest || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Request'}
                    </button>
                    {uploadMessage && <p className="mt-4 text-center">{uploadMessage}</p>}
                </div>
            </div>
        </section>
    );
};

export default FileUploader;
